import React from 'react';
import {
  activateAdminInvitation,
  AdminInvitation,
  createAdminInvitation,
  getAdminInvitations,
  rejectAdminInvitation,
  resendAdminInvitation,
} from '../../services/system';

const PAGE_SIZE = 50;

export function AdminInvitations({ searchQuery = '' }: { searchQuery?: string }) {
  const [invitations, setInvitations] = React.useState<AdminInvitation[]>([]);
  const [total, setTotal] = React.useState(0);
  const [offset, setOffset] = React.useState(0);
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery.trim());
  const [showInviteForm, setShowInviteForm] = React.useState(false);
  const [rejecting, setRejecting] = React.useState<AdminInvitation | null>(null);
  const [rejectionReason, setRejectionReason] = React.useState('');
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [activeId, setActiveId] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [reloadKey, setReloadKey] = React.useState(0);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [searchQuery]);

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    getAdminInvitations(PAGE_SIZE, offset, debouncedSearch)
      .then((response) => {
        if (isActive) {
          setInvitations(response.invitations);
          setTotal(response.total);
        }
      })
      .catch((requestError) => {
        if (isActive) setError(requestError instanceof Error ? requestError.message : 'Unable to load admin invitations');
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [debouncedSearch, offset, reloadKey]);

  const createInvitation = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActiveId('create');
    setError('');
    setMessage('');
    try {
      await createAdminInvitation({ full_name: name.trim(), email: email.trim() });
      setName('');
      setEmail('');
      setShowInviteForm(false);
      setMessage('Admin invitation created successfully.');
      setReloadKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to create admin invitation');
    } finally {
      setActiveId('');
    }
  };

  const activate = async (invitation: AdminInvitation) => {
    setActiveId(invitation.id);
    setError('');
    setMessage('');
    try {
      await activateAdminInvitation(invitation.id);
      setMessage(`${invitation.name} was activated.`);
      setReloadKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to activate admin invitation');
    } finally {
      setActiveId('');
    }
  };

  const reject = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!rejecting) return;
    setActiveId(rejecting.id);
    setError('');
    setMessage('');
    try {
      await rejectAdminInvitation(rejecting.id, rejectionReason.trim());
      setMessage(`${rejecting.name}'s invitation was rejected.`);
      setRejecting(null);
      setRejectionReason('');
      setReloadKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to reject admin invitation');
    } finally {
      setActiveId('');
    }
  };

  const resend = async (invitation: AdminInvitation) => {
    setActiveId(invitation.id);
    setError('');
    setMessage('');
    try {
      await resendAdminInvitation(invitation.id);
      setMessage(`A new invitation was sent to ${invitation.email}.`);
      setReloadKey((current) => current + 1);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to resend admin invitation');
    } finally {
      setActiveId('');
    }
  };

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + invitations.length, total);

  return (
    <div className="admin-invitations-page">
      <div className="section-head passengers-head">
        <div>
          <h2>Admin invitations</h2>
          <span>Invite, review, activate, and reject administrator accounts.</span>
        </div>
        <button className="btn btn-primary" type="button" onClick={() => setShowInviteForm(true)}>Invite admin</button>
      </div>

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="data-table admin-invitations-table">
        <table>
          <thead><tr><th>Administrator</th><th>Status</th><th>Invited</th><th>Actions</th></tr></thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={4}>Loading admin invitations...</td></tr>
            ) : invitations.length ? invitations.map((invitation) => (
              <tr key={invitation.id}>
                <td data-label="Administrator"><strong>{invitation.name}</strong><span className="admin-invitation-email">{invitation.email}</span></td>
                <td data-label="Status"><span className={`pill ${statusTone(invitation.status)}`}>{invitation.status}</span></td>
                <td data-label="Invited">{formatTime(invitation.createdAt)}</td>
                <td data-label="Actions">
                  <div className="admin-invitation-actions">
                    {canReviewInvitation(invitation.status) ? (
                      <>
                        <button className="btn btn-sm btn-primary" disabled={activeId === invitation.id} type="button" onClick={() => activate(invitation)}>
                          {activeId === invitation.id ? 'Working...' : 'Activate'}
                        </button>
                        <button className="btn btn-sm passenger-deactivate-btn" disabled={activeId === invitation.id} type="button" onClick={() => setRejecting(invitation)}>Reject</button>
                        {canResendInvitation(invitation.status) ? (
                          <button className="btn btn-sm" disabled={activeId === invitation.id} type="button" onClick={() => resend(invitation)}>
                            {activeId === invitation.id ? 'Resending...' : 'Resend'}
                          </button>
                        ) : null}
                      </>
                    ) : canResendInvitation(invitation.status) ? (
                      <button className="btn btn-sm btn-primary" disabled={activeId === invitation.id} type="button" onClick={() => resend(invitation)}>
                        {activeId === invitation.id ? 'Resending...' : 'Resend invitation'}
                      </button>
                    ) : <span className="muted">Managed in Admin users</span>}
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td className="kyc-table-state" colSpan={4}>{debouncedSearch ? `No invitations match "${debouncedSearch}".` : 'No admin invitations found.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="passengers-pagination">
        <span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span>
        <div>
          <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
          <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
        </div>
      </div>

      {showInviteForm ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setShowInviteForm(false)}>
          <form className="signup-dialog" role="dialog" aria-modal="true" aria-labelledby="invite-admin-title" onMouseDown={(event) => event.stopPropagation()} onSubmit={createInvitation}>
            <div className="signup-dialog-head"><div><h2 id="invite-admin-title">Invite administrator</h2></div><button className="btn-icon borderless" type="button" aria-label="Close invitation form" onClick={() => setShowInviteForm(false)}>&times;</button></div>
            <label className="form-group"><span>Full name</span><input required value={name} onChange={(event) => setName(event.target.value)} /></label>
            <label className="form-group"><span>Email address</span><input required type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
            <div className="kyc-dialog-actions"><button className="btn" type="button" onClick={() => setShowInviteForm(false)}>Cancel</button><button className="btn btn-primary" disabled={activeId === 'create'} type="submit">{activeId === 'create' ? 'Inviting...' : 'Create invitation'}</button></div>
          </form>
        </div>
      ) : null}

      {rejecting ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setRejecting(null)}>
          <form className="signup-dialog" role="dialog" aria-modal="true" aria-labelledby="reject-invitation-title" onMouseDown={(event) => event.stopPropagation()} onSubmit={reject}>
            <div className="signup-dialog-head"><div><h2 id="reject-invitation-title">Reject invitation</h2><span>{rejecting.name}</span></div><button className="btn-icon borderless" type="button" aria-label="Close rejection form" onClick={() => setRejecting(null)}>&times;</button></div>
            <label className="form-group"><span>Reason</span><textarea required value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} /></label>
            <div className="kyc-dialog-actions"><button className="btn" type="button" onClick={() => setRejecting(null)}>Cancel</button><button className="btn btn-danger" disabled={!rejectionReason.trim() || activeId === rejecting.id} type="submit">Reject invitation</button></div>
          </form>
        </div>
      ) : null}

    </div>
  );
}

function statusTone(status: string) {
  const normalizedStatus = normalizeStatus(status);
  if (normalizedStatus === 'active' || normalizedStatus === 'activated') return 'success';
  if (normalizedStatus === 'rejected' || normalizedStatus === 'revoked' || normalizedStatus === 'expired') return 'danger';
  return 'warn';
}

export function canReviewInvitation(status: string) {
  return !['active', 'activated', 'rejected', 'revoked', 'expired'].includes(normalizeStatus(status));
}

export function canResendInvitation(status: string) {
  return ['pending', 'pending_verification', 'sent', 'expired'].includes(normalizeStatus(status));
}

function normalizeStatus(status: string) {
  return status.trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function formatTime(value: string) {
  const date = new Date(value);
  return value && !Number.isNaN(date.getTime()) ? date.toLocaleString() : 'Not provided';
}
